import React, { useEffect, useState, useRef } from "react";

// Use the real shared module for the production sidebar
import { Sidebar } from "@shared-modules";
// No direct module imports: modules render via MODULE_VIEWS; pages via PAGES.
import { Suspense } from "react";
import DebugPanel, { DebugBanner, BuildMismatchBanner, installGlobalErrorCapture, pushDebugError } from "./components/DebugPanel.jsx";
import SystemStatusBar from "./components/SystemStatusBar.jsx";
import ModuleTodosTable from "./components/ModuleTodosTable.jsx";

// Modules rendering surface is decoupled into a separate component
import ModulesSurface from "./modules/ModulesSurface.jsx";

// Early theme bootstrap (pre-render): apply last saved theme from localStorage to avoid
// a flash of the default palette and ensure persistence even if /api/auth/me is slow.
try {
  const raw = localStorage.getItem('agentTheme');
  if (raw) {
    const j = JSON.parse(raw);
    const hex1 = typeof j?.theme_color === 'string' ? j.theme_color : null;
    const hex2 = typeof j?.theme_color2 === 'string' ? j.theme_color2 : null;
    const el = document.documentElement;
    const toRgb = (h) => { const s=h.replace('#',''); const n=parseInt(s.length===3?s.split('').map(c=>c+c).join(''):s,16); return { r:(n>>16)&255, g:(n>>8)&255, b:n&255 }; };
    const clamp=(x,a,b)=>Math.max(a,Math.min(b,x));
    const shade=(rgb,f)=>({ r:clamp(Math.round(rgb.r*f),0,255), g:clamp(Math.round(rgb.g*f),0,255), b:clamp(Math.round(rgb.b*f),0,255) });
    const toCss=(c)=>`rgb(${c.r} ${c.g} ${c.b})`;
    if (hex1) {
      const rgb = toRgb(hex1); const rgb700 = shade(rgb,0.8);
      el.style.setProperty('--brand-600', toCss(rgb));
      el.style.setProperty('--brand-700', toCss(rgb700));
      el.style.setProperty('--brand-50', `color-mix(in srgb, ${toCss(rgb)} 10%, white)`);
      el.style.setProperty('--brand-100', `color-mix(in srgb, ${toCss(rgb)} 18%, white)`);
      el.style.setProperty('--brand-200', `color-mix(in srgb, ${toCss(rgb)} 28%, white)`);
    }
    if (hex2) {
      const rgb = toRgb(hex2); const rgb700 = shade(rgb,0.8);
      el.style.setProperty('--brand2-600', toCss(rgb));
      el.style.setProperty('--brand2-700', toCss(rgb700));
      el.style.setProperty('--brand2-50', `color-mix(in srgb, ${toCss(rgb)} 10%, white)`);
      el.style.setProperty('--brand2-100', `color-mix(in srgb, ${toCss(rgb)} 18%, white)`);
    }
  }
} catch {}

// Removed inline module loaders to keep App shell decoupled from module code.

// Discover top-level pages from /pages/<pageId>/index.* (relative to frontend/src -> ../pages)
const __appPages = import.meta.glob('../pages/*/index.{js,jsx,ts,tsx}');
const PAGES = {};
for (const [p, loader] of Object.entries(__appPages)) {
  try {
    const parts = p.split('/pages/')[1].split('/');
    const pageId = parts[0];
    PAGES[pageId] = React.lazy(async () => {
      try {
        const m = await loader();
        const Cmp = m?.default || m?.Main;
        if (!Cmp) {
          const msg = `Page not found: ${pageId}`;
          try { pushDebugError({ source: `page:${pageId}`, error: msg }); } catch {}
          return { default: () => (<div className="p-4 text-sm text-red-600">{msg}</div>) };
        }
        return { default: Cmp };
      } catch (e) {
        const errMsg = String(e?.message || e);
        try { pushDebugError({ source: `page:${pageId}`, error: errMsg, stack: String(e?.stack || '') }); } catch {}
        return { default: () => (
          <div className="p-4 text-sm text-red-600">
            <div className="font-semibold">Page failed to load: {pageId}</div>
            <div className="mt-1 text-xs text-red-700 break-all">{errMsg}</div>
            <button onClick={() => { try { window.location.reload(); } catch {} }} className="mt-2 text-xs underline">Reload</button>
          </div>
        ) };
      }
    });
  } catch {}
}
// Dynamic modules are loaded via MODULE_VIEWS below; avoid hardcoded module imports.

class DebugBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    try {
      console.error('[DebugBoundary]', this.props.name || 'boundary', error, info?.componentStack);
      try { pushDebugError({ source: this.props.name || 'boundary', error: String(error?.message || error), stack: String(info?.componentStack || ''), when: Date.now() }); } catch {}
    } catch {}
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-sm text-red-600">
          {this.props.name || 'Component'} error. See console for details.
        </div>
      );
    }
    return this.props.children;
  }
}
// DbManager is rendered via a dynamic page wrapper (pages/db/index.jsx)

import { loadUIState, saveUIState, setCurrentAgentId, getDeviceId, installUIStateFlush, flushUIStateNow, loadModuleState, saveModuleState } from "@app-lib/uiState";

// Removed static TAB_META; titles/descriptions are sourced dynamically
// from breadcrumbs, module manifests, or derived from the route.

const MODULE_ROUTE_ALIASES = Object.freeze({
  "website-translator": "translator-blog",
});

const MODULE_ROUTE_CANONICAL = Object.freeze({
  "translator-blog": "website-translator",
});

function toModuleIdFromRoute(routeSegment = "") {
  const key = String(routeSegment || "").trim();
  return MODULE_ROUTE_ALIASES[key] || key;
}

function toRouteFromModuleId(moduleId = "") {
  const key = String(moduleId || "").trim();
  return MODULE_ROUTE_CANONICAL[key] || key;
}

function normalizeHashModuleAlias(hashValue = "") {
  try {
    const original = String(hashValue || "").trim();
    if (!original.startsWith("#")) return original;
    const raw = original.replace(/^#\/?/, "");
    if (!raw) return original;
    const qIdx = raw.indexOf("?");
    const pathPart = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
    const queryPart = qIdx >= 0 ? raw.slice(qIdx) : "";
    const parts = pathPart.split("/").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return original;
    if (parts[0] === "modules" && parts[1]) {
      parts[1] = toRouteFromModuleId(parts[1]);
    } else {
      parts[0] = toRouteFromModuleId(parts[0]);
    }
    return "#/" + parts.join("/") + queryPart;
  } catch {
    return String(hashValue || "");
  }
}

export default function App() {
  const [me, setMe] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const baseTitleRef = useRef(typeof document !== 'undefined' ? (document.title || 'Knowledge Builder') : 'Knowledge Builder');
  const [dbUnavailable, setDbUnavailable] = useState(false);
  // Determine if sidebar has DB items; hide nav/topbar when empty
  const [hasSidebar, setHasSidebar] = useState(null); // null = unknown, boolean after fetch
  const [activeTab, setActiveTab] = useState('');
  const [navTick, setNavTick] = useState(0);
  const [activeModules, setActiveModules] = useState(new Set());
  const [modulesLoaded, setModulesLoaded] = useState(false);
  // Global runtime error capture (idempotent)
  useEffect(() => { try { installGlobalErrorCapture(); } catch {} }, []);

  // Seed active tab early from current hash to avoid "Select a page" flash on module reloads
  useEffect(() => {
    try {
      const normalizedHash = normalizeHashModuleAlias(window.location.hash || "");
      if (normalizedHash && normalizedHash !== window.location.hash) {
        window.history.replaceState(null, "", normalizedHash);
      }
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const parts = raw.split('/').filter(Boolean);
      const deepLinked = (parts[0] === 'modules' && parts[1]) || (parts[0] && !PAGES[parts[0]]);
      if (deepLinked) setActiveTab('modules');
    } catch {}
  }, []);

  // Load active modules (used to guard dynamic renders and data fetches)
  useEffect(() => {
    if (!me) { setActiveModules(new Set()); setModulesLoaded(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/modules', { credentials: 'include' });
        if (!res.ok) { if (!cancelled) setModulesLoaded(false); return; }
        const j = await res.json();
        const list = Array.isArray(j.modules) ? j.modules : [];
        const set = new Set(list.filter(m => m && (m.active === true || m.active === 1)).map(m => String(m.id || m.name || '').trim()).filter(Boolean));
        set.add('module-manager');
        if (!cancelled) { setActiveModules(set); setModulesLoaded(true); }
      } catch {
        if (!cancelled) setModulesLoaded(false);
      }
    })();
    return () => { cancelled = true };
  }, [me]);

  // Check DB health periodically and surface an alert when unavailable
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const r = await fetch('/api/health/db', { credentials: 'include', cache: 'no-store' });
        const j = await r.json().catch(()=>({}));
        if (!r.ok) {
          const le = j?.last_error;
          const detail = le ? `${le.code ? `${le.code}: ` : ''}${String(le.message || '').trim()}`.trim() : '';
          throw new Error(detail || 'db_unavailable');
        }
        const ok = !!(j && j.ok && j.db);
        if (!cancelled) setDbUnavailable(!ok);
        if (!ok) {
          try {
            const le = j?.last_error;
            const detail = le ? `${le.code ? `${le.code}: ` : ''}${String(le.message || '').trim()}`.trim() : '';
            pushDebugError({ source: 'db_health', error: detail ? `Database unavailable: ${detail}` : 'Database unavailable' });
          } catch {}
        }
      } catch (e) {
        if (!cancelled) setDbUnavailable(true);
        try { pushDebugError({ source: 'db_health', error: 'Database unavailable', stack: String(e?.message||e) }); } catch {}
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Minimal built-in login fallback in case the pages/login surface is not available
  function FallbackLogin() {
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [err, setErr] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const submit = async (e) => {
      e?.preventDefault?.(); setErr(''); setBusy(true);
      try {
        const r = await fetch('/api/auth/login', { method:'POST', headers:{ 'Content-Type':'application/json' }, credentials:'include', body: JSON.stringify({ email, password }) });
        if (!r.ok) throw new Error((await r.json().catch(()=>({error:'login_failed'}))).error || 'login_failed');
        // Verify session and jump to module manager
        try {
          const meRes = await fetch('/api/auth/me', { credentials:'include' });
          if (meRes.ok) {
            try { if (window.location.hash !== '#/module-manager') window.history.replaceState(null, '', '#/module-manager'); } catch {}
            try { window.location.reload(); } catch {}
            return;
          }
        } catch {}
        try { window.location.reload(); } catch {}
      } catch (e) { setErr(String(e?.message || e)); } finally { setBusy(false); }
    };
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow">
          <div className="login-panel__logo mb-3">LC</div>
          <div className="text-lg font-semibold mb-4">Se connecter</div>
          <label className="block text-sm text-gray-700 mb-1">Email</label>
          <input type="email" className="border rounded px-3 py-2 w-full mb-3" value={email} onChange={(e)=>setEmail(e.target.value)} />
          <label className="block text-sm text-gray-700 mb-1">Mot de passe</label>
          <input type="password" className="border rounded px-3 py-2 w-full mb-4" value={password} onChange={(e)=>setPassword(e.target.value)} />
          {err && <div className="text-xs text-red-600 mb-3">{err}</div>}
          <button type="submit" disabled={busy} className="w-full rounded bg-[color:var(--brand-600)] px-3 py-2 text-white hover:bg-[color:var(--brand-700)]">
            {busy ? '...' : 'Connexion'}
          </button>
        </form>
      </div>
    );
  }

  // Probe sidebar once on mount
  useEffect(() => {
    // Normalize legacy '#/agent' to '#/agents' immediately on mount
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const root = raw.split('/')[0] || '';
      if (root === 'agent') {
        const fixed = '#/agents';
        if (window.location.hash !== fixed) {
          window.history.replaceState(null, '', fixed);
        }
      }
    } catch {}

    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set('t', String(Date.now()));
        const res = await fetch('/api/sidebar/tree?' + params.toString(), { credentials: 'include' });
        if (!cancelled && res.ok) {
          const j = await res.json();
          setHasSidebar(Array.isArray(j.items) && j.items.length > 0);
        } else if (!cancelled) {
          setHasSidebar(false);
        }
      } catch {
        if (!cancelled) setHasSidebar(false);
      }
    })();
    return () => { cancelled = true };
  }, []);

  // After we know sidebar presence, seed initial active tab
  useEffect(() => {
    if (hasSidebar === null) return; // not decided yet
    if (hasSidebar === false) {
      // Sidebar empty: show Modules surface but do not clobber an existing deep link (e.g., '#/logs2')
      try {
        const raw = String(window.location.hash || '').replace(/^#\/?/, '');
        if (!raw) {
          if (window.location.hash !== '#/module-manager') window.history.replaceState(null, '', '#/module-manager');
        }
      } catch {}
      setActiveTab('modules');
      return;
    }
    try {
      // Prefer bootstrap seed; ignore localStorage to avoid stale tabs for guests
      const predicted = sessionStorage.getItem('app_initial_tab');
      const next = predicted || 'login';
      setActiveTab(next);
    } catch { setActiveTab('login'); }
  }, [hasSidebar]);

  // Ensure hash navigation opens the right main tab (e.g., #/modules/<id>)
  useEffect(() => {
    if (!hasSidebar) return; // do not force tabs when sidebar is empty
    const applyFromHash = () => {
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const parts = raw.split('/').filter(Boolean);
      let root = parts[0] || '';
      if (root === 'agent') {
        // Normalize legacy '#/agent' to '#/agents' (module route)
        const fixed = '#/agents';
        if (window.location.hash !== fixed) {
          window.history.replaceState(null, '', fixed);
        }
        root = 'modules';
      }
      // Treat any unknown root (not a top-level Page) as a module deep link.
      const known = new Set(['modules', ...Object.keys(PAGES)]);
      if (!known.has(root) && root) {
        setActiveTab('modules');
        return;
      }
      if (known.has(root)) setActiveTab(root);
      else setActiveTab('modules');
    } catch {}
    };
    applyFromHash();
    const onHash = () => { applyFromHash(); try { setNavTick((t) => t + 1); } catch {} };
    const onNavigate = () => { applyFromHash(); try { setNavTick((t) => t + 1); } catch {} };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('app:navigate', onNavigate);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('app:navigate', onNavigate);
    };
  }, [hasSidebar]);
  useEffect(() => {
    try { localStorage.setItem('app_active_tab', activeTab); } catch {}
    // Also persist per-agent tab selection when authenticated
    try {
      if (me && me.id != null) { saveUIState(me.id, { activeTab }); flushUIStateNow(me.id); }
    } catch {}
  }, [activeTab, me]);
  // Clear breadcrumb on main tab change; child views will set specific trails
  useEffect(() => {
    setBreadcrumb([]);
  }, [activeTab]);

  // Listen for breadcrumb change events from child views
  useEffect(() => {
    const onCrumb = (e) => {
      try {
        const trail = e?.detail;
        if (!trail) { setBreadcrumb([]); return; }
        const arr = Array.isArray(trail) ? trail.filter(Boolean).map(String) : [String(trail)];
        setBreadcrumb(arr);
      } catch {}
    };
    window.addEventListener('app-breadcrumb', onCrumb);
    return () => window.removeEventListener('app-breadcrumb', onCrumb);
  }, []);

  // Keep URL hash minimally in sync (#/<tab>). When viewing modules, never rewrite the hash
  // so that direct module routes like #/<module_id> remain intact.
  useEffect(() => {
    try {
      // If not authenticated, pin hash to '#/login' and avoid any other rewrites
      if (!me) {
        const raw = String(window.location.hash || '').replace(/^#\/?/, '');
        if (!raw || /^login(\/|$)?/i.test(raw)) {
          if (window.location.hash !== '#/login') {
            try { window.history.replaceState(null, '', '#/login'); } catch {}
          }
        }
        return;
      }
      // Do not rewrite hash while on modules surface; preserve deep links like #/<module_id>
      if (activeTab === 'modules') return;

      // Preserve deep routes for tabs that include additional path segments (e.g. '#/tools/Email').
      // If the current hash already starts with the active tab and has extra segments, keep it.
      try {
        const raw = String(window.location.hash || '').replace(/^#\/?/, '');
        if (raw) {
          const root = raw.split('/')[0] || '';
          if (root === activeTab) {
            const rest = raw.slice(root.length);
            // Keep '#/<tab>/...' and '#/<tab>?...' intact.
            if (rest.startsWith('/') || rest.startsWith('?')) return;
          }
        }
      } catch {}

      let segments = [activeTab];
      const nextHash = '#' + (segments.length ? ('/' + segments.join('/')) : '');
      if (window.location.hash !== nextHash) {
        window.history.replaceState(null, '', nextHash);
      }
    } catch {}
  }, [activeTab, breadcrumb]);

  // Persist and restore per-tab scroll position across reloads
  useEffect(() => {
    const key = (t) => `__scroll_${t}`;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        try { sessionStorage.setItem(key(activeTab), String(window.scrollY || window.pageYOffset || 0)); } catch {}
        ticking = false;
      });
    };
    // Restore when tab is set/mounted
    try {
      const raw = sessionStorage.getItem(key(activeTab));
      const y = raw != null ? Number(raw) : 0;
      if (!Number.isNaN(y)) window.scrollTo(0, Math.max(0, y));
    } catch {}
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeTab]);
  const restoreStateRef = useRef(null);
  const desiredTabRef = useRef(null);

  // Respond to hash changes (deep link navigation)
  useEffect(() => {
    const onHash = () => {
      try {
        const normalizedHash = normalizeHashModuleAlias(window.location.hash || "");
        if (normalizedHash && normalizedHash !== window.location.hash) {
          window.history.replaceState(null, "", normalizedHash);
        }
        // If unauthenticated, always pin to login and stop processing
        if (!me) {
          if (window.location.hash !== '#/login') {
            try { window.history.replaceState(null, '', '#/login'); } catch {}
          }
          if (activeTab !== 'login') setActiveTab('login');
          return;
        }
        // Normalize legacy hashes like "#modules/..." -> "#/modules/..."
        // No legacy hash normalization; routes are handled dynamically.

        // Note: keep google-api/ads inside google-api (do not auto-redirect to google-ads-manager)

        const raw = String((normalizedHash || window.location.hash) || '').replace(/^#\/?/, '');
        const parts = raw.split('/').map(decodeURIComponent).filter(Boolean);
        const next = parts[0];
        if (!next) return;
        const known = new Set([ 'modules', ...Object.keys(PAGES) ]);
        if (known.has(next)) {
          if (next !== activeTab) setActiveTab(next);
        } else {
          // Treat unknown top-level as a module id alias: "#/logs2" -> "#/modules/logs2"
          // Also supports legacy "#logs2" (without slash) due to the regex above.
          const modId = toModuleIdFromRoute(next);
          if (modId) {
            if (activeTab !== 'modules') setActiveTab('modules');
            // Persist last module route for auto-restore after auth
            try { if (me && me.id != null) { saveModuleState('modules', { subModule: modId, path: parts.slice(1) }, me.id); flushUIStateNow(me.id); } } catch {}
            const detail = { modules: { modules: { subModule: modId, path: parts.slice(1) } } };
            try { window.dispatchEvent(new CustomEvent('app-restore', { detail })); } catch {}
            return;
          }
        }
        // Support deep-links for Modules sub-pages like #/modules/logs2
        if (next === 'modules') {
          if (activeTab !== 'modules') setActiveTab('modules');
          const modId = parts[1] ? toModuleIdFromRoute(parts[1]) : null;
          // Persist last module route (submodule + optional path)
          try { if (modId && me && me.id != null) { saveModuleState('modules', { subModule: modId, path: parts.slice(2) }, me.id); flushUIStateNow(me.id); } } catch {}
          const detail = { modules: { modules: { subModule: modId, path: parts.slice(2) } } };
          try { window.dispatchEvent(new CustomEvent('app-restore', { detail })); } catch {}
        }
      } catch {}
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [activeTab]);

  // Restore last UI state (per-agent) after authentication
  useEffect(() => {
    if (!me || !me.id) return;
    // 0) Apply one-shot tab from login prediction immediately
    try {
      const pred = sessionStorage.getItem('app_initial_tab');
      if (pred && pred !== activeTab) setActiveTab(pred);
      try {
        const sub = sessionStorage.getItem("app_initial_automations_subtab");
        if (pred === "automations" && sub) {
          window.dispatchEvent(new CustomEvent("app-restore", { detail: { modules: { automations: { subTab: sub } } } }));
        }
      } catch {}
      // Also auto-restore last module route if present in saved UI state,
      // but only when there is no explicit deep link in the current URL.
      try {
        // Detect an explicit deep link (e.g., '#/gateway' or '#/modules/gateway')
        let hasDeepLink = false;
        try {
          const rawHash = String(window.location.hash || '').replace(/^#\/?/, '');
          const segs = rawHash.split('/').filter(Boolean);
          if (segs.length) {
            // Treat any explicit hash as intent, including page routes like '#/gateway'
            hasDeepLink = (segs[0] === 'modules' ? !!segs[1] : true);
          }
        } catch {}
        const stLocal = loadUIState(me.id) || {};
        const last = stLocal && stLocal.modules && stLocal.modules.modules ? stLocal.modules.modules : null;
        if (!hasDeepLink && last && last.subModule) {
          if (activeTab !== 'modules') setActiveTab('modules');
          const pathArr = Array.isArray(last.path) ? last.path : [];
          // Ensure URL reflects the desired module so reloads land on the module page
          try {
            const segs = [toRouteFromModuleId(last.subModule), ...pathArr].map(encodeURIComponent).join('/');
            const desiredHash = `#/${segs}`;
            if (window.location.hash !== desiredHash) {
              window.history.replaceState(null, '', desiredHash);
            }
          } catch {}
          const detail = { modules: { modules: { subModule: last.subModule, path: pathArr } } };
          window.dispatchEvent(new CustomEvent('app-restore', { detail }));
        }
      } catch {}
    } catch {}
      try {
        const stLocal = loadUIState(me.id) || {};
        const stServer = (me && me.ui_state && typeof me.ui_state === 'object') ? me.ui_state : {};
        const mergedModules = { ...(stServer.modules || {}), ...(stLocal.modules || {}) };
        let st = { ...stServer, ...stLocal, modules: mergedModules };
        if (stServer && typeof stServer === 'object') {
          // Sidebar visibility/settings are centrally managed by Module Manager (DB-backed).
          // Ignore any legacy per-agent sidebar preferences present in ui_state.
          if (stServer.agent_sections && typeof stServer.agent_sections === 'object') st.agent_sections = stServer.agent_sections;
        }
        // Persist merged state locally
        try { saveUIState(me.id, st); } catch {}
        const adminOnly = new Set(["modules", "ha"]);
        const desiredTab = st.activeTab || 'modules';
        // Do not override an explicit deep link (e.g., '#/gateway') on reload
        let hasDeepLinkNow = false;
        try {
          const rawHashNow = String(window.location.hash || '').replace(/^#\/?/, '');
          const segs = rawHashNow.split('/').filter(Boolean);
          hasDeepLinkNow = segs.length > 0;
        } catch {}
        restoreStateRef.current = st;
        desiredTabRef.current = desiredTab;
        if (desiredTab && !hasDeepLinkNow) {
          const allowed = adminOnly.has(desiredTab) ? (me.role === 'admin') : true;
          if (allowed && desiredTab !== activeTab) setActiveTab(desiredTab);
        }
      } catch {}
  }, [me]);

  // Fire app-restore once the desired activeTab is actually mounted
  useEffect(() => {
    try {
      if (!me || !desiredTabRef.current || !restoreStateRef.current) return;
      if (activeTab === desiredTabRef.current) {
        window.dispatchEvent(new CustomEvent('app-restore', { detail: restoreStateRef.current }));
        // Prevent duplicate re-broadcasts
        restoreStateRef.current = null;
        desiredTabRef.current = null;
        try { sessionStorage.removeItem('app_initial_tab'); } catch {}
      }
    } catch {}
  }, [activeTab, me]);

  // Apply theme from user preferences
  const applyTheme = (hex = '#9bb5edff') => {
    try {
      const el = document.documentElement;
      const toRgb = (h) => { const s=h.replace('#',''); const n=parseInt(s.length===3?s.split('').map(c=>c+c).join(''):s,16); return { r:(n>>16)&255, g:(n>>8)&255, b:n&255 }; };
      const clamp=(x,a,b)=>Math.max(a,Math.min(b,x));
      const shade=(rgb,f)=>({ r:clamp(Math.round(rgb.r*f),0,255), g:clamp(Math.round(rgb.g*f),0,255), b:clamp(Math.round(rgb.b*f),0,255) });
      const rgb = toRgb(hex); const rgb700 = shade(rgb,0.8); const toCss=(c)=>`rgb(${c.r} ${c.g} ${c.b})`;
      el.style.setProperty('--brand-600', toCss(rgb));
      el.style.setProperty('--brand-700', toCss(rgb700));
      el.style.setProperty('--brand-50', `color-mix(in srgb, ${toCss(rgb)} 10%, white)`);
      el.style.setProperty('--brand-100', `color-mix(in srgb, ${toCss(rgb)} 18%, white)`);
      el.style.setProperty('--brand-200', `color-mix(in srgb, ${toCss(rgb)} 28%, white)`);
    } catch {}
  };
  const applyTheme2 = (hex = '#92e6dfff') => {
    try {
      const el = document.documentElement;
      const toRgb = (h) => { const s=h.replace('#',''); const n=parseInt(s.length===3?s.split('').map(c=>c+c).join(''):s,16); return { r:(n>>16)&255, g:(n>>8)&255, b:n&255 }; };
      const clamp=(x,a,b)=>Math.max(a,Math.min(b,x));
      const shade=(rgb,f)=>({ r:clamp(Math.round(rgb.r*f),0,255), g:clamp(Math.round(rgb.g*f),0,255), b:clamp(Math.round(rgb.b*f),0,255) });
      const rgb = toRgb(hex); const rgb700 = shade(rgb,0.8); const toCss=(c)=>`rgb(${c.r} ${c.g} ${c.b})`;
      el.style.setProperty('--brand2-600', toCss(rgb));
      el.style.setProperty('--brand2-700', toCss(rgb700));
      el.style.setProperty('--brand2-50', `color-mix(in srgb, ${toCss(rgb)} 10%, white)`);
      el.style.setProperty('--brand2-100', `color-mix(in srgb, ${toCss(rgb)} 18%, white)`);
    } catch {}
  };

  // Whenever user loads/changes, apply theme colors
  useEffect(() => {
    if (me) {
      let c1 = me.theme_color || null;
      let c2 = me.theme_color2 || null;
      try {
        if (!c1 || !c2) {
          const raw = localStorage.getItem('agentTheme');
          if (raw) {
            const j = JSON.parse(raw);
            if (!c1 && typeof j?.theme_color === 'string') c1 = j.theme_color;
            if (!c2 && typeof j?.theme_color2 === 'string') c2 = j.theme_color2;
          }
        }
      } catch {}
      if (!c1) c1 = '#2563eb';
      if (!c2) c2 = '#0d9488';
      applyTheme(c1);
      applyTheme2(c2);
      try { localStorage.setItem('agentTheme', JSON.stringify({ theme_color: c1, theme_color2: c2 })); } catch {}
    }
  }, [me]);

  // Load current session
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(j => setMe(j))
      .catch(() => setMe(null))
      .finally(() => setAuthLoaded(true));
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!me) {
      setOrganization(null);
      return () => { mounted = false; };
    }
    const fallback = { org_id: me.org_id || 'org_default', name: '' };
    setOrganization(fallback);
    fetch('/api/agents/organization', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (!mounted) return;
        const org = j?.organization || null;
        setOrganization(org ? { ...fallback, ...org } : fallback);
      })
      .catch(() => {
        if (mounted) setOrganization(fallback);
      });
    return () => { mounted = false; };
  }, [me?.id, me?.org_id]);

  // Keep current agent id available for module-level persistence
  useEffect(() => {
    try { setCurrentAgentId(me && me.id != null ? me.id : null); } catch {}
    if (me && me.id != null) {
      try { installUIStateFlush(me.id); } catch {}
    }
  }, [me]);

  // After successful authentication, if the URL is still '#/login',
  // restore the initial deep link when available; otherwise go to Module Manager.
  useEffect(() => {
    try {
      if (authLoaded && me) {
        const hash = String(window.location.hash || '');
        if (/^#\/?login(\/|$)?/i.test(hash)) {
          let target = '#/module-manager';
          try {
            const seed = sessionStorage.getItem('app_initial_tab');
            if (seed && seed !== 'login') target = `#/${toRouteFromModuleId(seed)}`;
          } catch {}
          if (hash !== target) {
            window.history.replaceState(null, '', target);
          }
          setActiveTab('modules');
        }
      }
    } catch {}
  }, [authLoaded, me]);

  useEffect(() => {
    if (window.twemoji) window.twemoji.parse(document.body, { folder: "svg", ext: ".svg" });
  });

  const toTitle = (s = '') => String(s).split('-').map(w => (w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
  const currentTitle = (() => {
    if (Array.isArray(breadcrumb) && breadcrumb.length) return breadcrumb[0];
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const parts = raw.split('/').filter(Boolean);
      // If hash points to a module (unknown top-level page), use its id as title
      if (parts[0] && !PAGES[parts[0]]) return toTitle(parts[0]);
    } catch {}
    return toTitle(activeTab || '');
  })();
  useEffect(() => {
    try {
      const base = baseTitleRef.current || 'Livechat';
      const title = (Array.isArray(breadcrumb) && breadcrumb.length)
        ? breadcrumb.join(' > ')
        : currentTitle;
      document.title = title ? title : base;
    } catch {}
  }, [breadcrumb, currentTitle]);
  const renderPanel = (children, options = {}) => {
    const { scrollable = false, className = "", bodyClassName = "", fill = true } = options;
    const outerClass = ["panel", fill ? "h-full" : "h-auto", className].filter(Boolean).join(" ");
    const bodyClass = ["panel__body", scrollable ? "panel__body--scroll" : "", bodyClassName].filter(Boolean).join(" ");
    return (
      <div className={outerClass}>
        <div className={bodyClass}>{children}</div>
      </div>
    );
  };
  const organizationLabel = (() => {
    const orgId = String(organization?.org_id || me?.org_id || '').trim();
    const name = String(organization?.name || '').trim();
    if (name && orgId && name !== orgId) return `${name} (${orgId})`;
    return name || orgId;
  })();

  const renderMain = () => {
    // No hardcoded module surfaces here; handled via MODULE_VIEWS
    // Special-case only when no page exists for '#/gateway'
    try {
      const hasGatewayPage = !!PAGES['gateway'];
      if (!hasGatewayPage) {
        const raw = String(window.location.hash || '').replace(/^#\/?/, '');
        const parts = raw.split('/').filter(Boolean);
        const isGatewayLink = (parts[0] === 'gateway') || (parts[0] === 'modules' && parts[1] === 'gateway');
        if (isGatewayLink) {
          return renderPanel(
            <ModulesSurface me={me} activeModules={activeModules} modulesLoaded={modulesLoaded} navTick={navTick} />,
            { scrollable: true }
          );
        }
      }
    } catch {}
    if (activeTab === "modules") {
      // Delegate module rendering to decoupled surface
      return renderPanel(
        <ModulesSurface me={me} activeModules={activeModules} modulesLoaded={modulesLoaded} navTick={navTick} />,
        { scrollable: true }
      );
    }
    // DB page is provided via PAGES['db']
  // Render app pages at #/<pageId>
  if (PAGES[activeTab]) {
    const Page = PAGES[activeTab];
    return renderPanel(
      <Suspense fallback={<div className="p-4 text-sm text-gray-500">Loading…</div>}>
        <Page />
      </Suspense>,
      { scrollable: true }
    );
  }
    // Fallback: if URL points to a module, delegate to ModulesSurface even if activeTab
    // isn’t set yet (prevents "Select a page…" on reload of deep-linked modules)
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const parts = raw.split('/').filter(Boolean);
      const deepLinked = (parts[0] === 'modules' && parts[1]) || (parts[0] && !PAGES[parts[0]]);
      if (deepLinked) {
        return renderPanel(
          <ModulesSurface me={me} activeModules={activeModules} modulesLoaded={modulesLoaded} navTick={navTick} />,
          { scrollable: true }
        );
      }
    } catch {}
    // Fallback: try to render Module Manager for admins, else show a gentle hint
    try {
      if (me?.role === 'admin' && MODULE_VIEWS['module-manager']) {
        const ModPage = MODULE_VIEWS['module-manager'];
        const isValid = ModPage && (typeof ModPage === 'function' || typeof ModPage === 'object');
        if (isValid) {
          return renderPanel(
            <Suspense fallback={<div className="p-4 text-sm text-gray-500">Loading module manager…</div>}>
              <ModPage key={`module-manager:${navTick}`} />
            </Suspense>,
            { scrollable: true }
          );
        }
      }
    } catch {}
    return (<div className="p-4 text-sm text-gray-600">Select a page from the sidebar.</div>);
  };

  if (!authLoaded) return null;
  if (!me) {
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      // Preserve deep link if present; only force '#/login' when empty or already login
      if (!raw || /^login(\/|$)?/i.test(raw)) {
        if (window.location.hash !== '#/login') {
          window.history.replaceState(null, '', '#/login');
        }
      }
    } catch {}
    const Page = PAGES['login'];
    const isValid = Page && (typeof Page === 'function' || typeof Page === 'object');
    if (isValid) {
      return (
        <Suspense fallback={<div className="p-4 text-sm text-gray-500">Loading…</div>}>
          <Page />
        </Suspense>
      );
    }
    // Safe fallback if pages/login could not be resolved or is invalid
    return <FallbackLogin />;
  }

  // Detect when Module Manager is the active module/page to enable perf CSS tweaks
  const isModuleManagerRoute = (() => {
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const parts = raw.split('/').filter(Boolean);
      if (!parts.length) return false;
      if (parts[0] === 'modules') return parts[1] === 'module-manager';
      return parts[0] === 'module-manager';
    } catch { return false; }
  })();

  const currentModuleId = (() => {
    try {
      const raw = String(window.location.hash || '').replace(/^#\/?/, '');
      const parts = raw.split('/').filter(Boolean);
      if (!parts.length) return '';
      if (parts[0] === 'modules') return toModuleIdFromRoute(String(parts[1] || '').trim());
      if (parts[0] && !PAGES[parts[0]]) return toModuleIdFromRoute(String(parts[0] || '').trim());
      return '';
    } catch {
      return '';
    }
  })();
  const showModuleTodos = !!currentModuleId;

  return (
    <DebugBoundary name="app">
      <DebugBanner />
      {dbUnavailable && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="mx-auto max-w-screen-2xl bg-red-50 text-red-700 border-b border-red-200 px-4 py-2 text-sm flex items-center gap-3">
            <span className="inline-flex h-2 w-2 rounded-full bg-red-600" aria-hidden></span>
            <span>Base de données indisponible. Certaines fonctionnalités peuvent être limitées.</span>
          </div>
        </div>
      )}
    <div className={`app-shell ${isModuleManagerRoute ? 'perf-no-backdrop' : ''}`}>
      {/* Always render Sidebar; it will self-decide to show only brand/profile when empty */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        hasSidebar={hasSidebar}
        me={me}
        onLogout={async () => {
            try { if (me && me.id != null) await flushUIStateNow(me.id); } catch {}
            try { await fetch("/api/auth/logout", { method: "POST" }); } catch {}
            // Clear any persisted tab state so guests don't bounce to old tabs
            try { sessionStorage.removeItem('app_initial_tab'); } catch {}
            try { sessionStorage.removeItem('app_initial_automations_subtab'); } catch {}
            try { localStorage.removeItem('app_active_tab'); } catch {}
            // Force guest to login route immediately
            try {
              if (window.location.hash !== '#/login') {
                window.location.replace('#/login');
              }
            } catch {}
            setMe(null);
          }}
        onGoProfile={() => {
            try {
              const target = '#/agents';
              if (window.location.hash !== target) window.history.replaceState(null, '', target);
            } catch {}
            setActiveTab('modules');
          }}
      />

      <div className="app-shell__main" role="main">
        {hasSidebar ? (
        <header className={`app-topbar app-topbar--compact`}>
	          <div className={`app-topbar__copy ${showModuleTodos ? 'app-topbar__copy--wide' : ''}`}>
	            <div className="app-topbar__eyebrow">{(breadcrumb && breadcrumb.length ? breadcrumb : [currentTitle]).join(' > ')}</div>
	            {showModuleTodos ? (
	              <div className="mt-2">
	                <ModuleTodosTable moduleId={currentModuleId} title={`Todo list — ${currentModuleId}`} compact />
	              </div>
            ) : null}
          </div>
          <div className="app-topbar__aside">
            {me && organizationLabel && (
              <span className="mr-2 inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium" title={`Organization ${organizationLabel}`}>
                Organization: {organizationLabel}
              </span>
            )}
            {me && me.role === 'admin' && (
              <span className="mr-2 inline-flex items-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 text-[10px] font-medium" title={`Signed in as ${me.email || ''}`}>
                Signed in as {me.email || ''}
              </span>
            )}
            <SystemStatusBar me={me} />
          </div>
        </header>
        ) : null}

        <main className="app-shell__body">{renderMain()}</main>
      </div>
      <BuildMismatchBanner />
      <DebugPanel />
    </div>
    </DebugBoundary>
  );
}
